/**
 * Orders page component.
 */

import DownloadModal from '@/components/modals/DownloadModal';
import OrderDetailsModal from '@/components/modals/OrderDetailsModal';
import OrdersTable from '@/components/orders/OrdersTable';
import { apiClient } from '@/services/apiClient';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

interface SearchParams {
  search?: string;
  status?: string;
  device_id?: string;
  data_type?: string;
  date_from?: string;
  date_to?: string;
  page: number;
  per_page: number;
}

export default function OrdersPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useState<SearchParams>({
    page: 1,
    per_page: 20,
  });
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['orders', searchParams],
    queryFn: async () => {
      const response = await apiClient.getOrders(searchParams);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data;
    },
  });

  const handleSearch = () => {
    setSearchParams((prev) => ({ ...prev, page: 1 }));
    refetch();
  };

  const handleClear = () => {
    setSearchParams({
      page: 1,
      per_page: 20,
    });
  };

  const handleOrderClick = (orderId: number, action: 'details' | 'download') => {
    setSelectedOrderId(orderId);
    if (action === 'details') {
      setShowDetailsModal(true);
    } else {
      setShowDownloadModal(true);
    }
  };

  return (
    <div>
      <h2 className="mb-4" style={{ fontWeight: 600, color: 'var(--myant-text-primary)' }}>
        {t('orders.yourOrders')}
      </h2>
      <Card className="mb-4">
        <Card.Body>
          <h5 style={{ fontWeight: 600, marginBottom: '1.5rem', color: 'var(--myant-text-primary)' }}>
            {t('orders.searchOrders')}
          </h5>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>{t('common.search')}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t('orders.searchPlaceholder')}
                  value={searchParams.search || ''}
                  onChange={(e) =>
                    setSearchParams((prev) => ({ ...prev, search: e.target.value || undefined }))
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>{t('orders.deviceId')}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t('orders.deviceIdPlaceholder')}
                  value={searchParams.device_id || ''}
                  onChange={(e) =>
                    setSearchParams((prev) => ({ ...prev, device_id: e.target.value || undefined }))
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>{t('orders.status')}</Form.Label>
                <Form.Select
                  value={searchParams.status || ''}
                  onChange={(e) =>
                    setSearchParams((prev) => ({ ...prev, status: e.target.value || undefined }))
                  }
                >
                  <option value="">{t('orders.allStatuses')}</option>
                  <option value="PENDING">{t('status.pending')}</option>
                  <option value="INITIATED">{t('status.initiated')}</option>
                  <option value="SUBMITTED">{t('status.submitted')}</option>
                  <option value="IN_PROGRESS">{t('status.inProgress')}</option>
                  <option value="COMPLETED">{t('status.completed')}</option>
                  <option value="FAILED">{t('status.failed')}</option>
                  <option value="CANCELLED">{t('status.cancelled')}</option>
                  <option value="EXPIRED">{t('status.expired')}</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>{t('orders.dataType')}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t('orders.dataTypePlaceholder')}
                  value={searchParams.data_type || ''}
                  onChange={(e) =>
                    setSearchParams((prev) => ({ ...prev, data_type: e.target.value || undefined }))
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>{t('orders.fromDate')}</Form.Label>
                <Form.Control
                  type="date"
                  value={searchParams.date_from || ''}
                  onChange={(e) =>
                    setSearchParams((prev) => ({ ...prev, date_from: e.target.value || undefined }))
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>{t('orders.toDate')}</Form.Label>
                <Form.Control
                  type="date"
                  value={searchParams.date_to || ''}
                  onChange={(e) =>
                    setSearchParams((prev) => ({ ...prev, date_to: e.target.value || undefined }))
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <Button variant="primary" onClick={handleSearch} className="me-2">
                {t('common.search')}
              </Button>
              <Button variant="outline-secondary" onClick={handleClear}>
                {t('common.clear')}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Button variant="primary" onClick={() => refetch()} className="mb-3">
        {t('common.refresh')}
      </Button>
      {isLoading ? (
        <Alert variant="info">{t('orders.loadingOrders')}</Alert>
      ) : (
        <OrdersTable
          data={data}
          onOrderClick={handleOrderClick}
          onPageChange={(page) => setSearchParams((prev) => ({ ...prev, page }))}
        />
      )}
      {showDetailsModal && selectedOrderId && (
        <OrderDetailsModal
          show={showDetailsModal}
          onHide={() => {
            setShowDetailsModal(false);
            setSelectedOrderId(null);
          }}
          orderId={selectedOrderId}
        />
      )}
      {showDownloadModal && selectedOrderId && (
        <DownloadModal
          show={showDownloadModal}
          onHide={() => {
            setShowDownloadModal(false);
            setSelectedOrderId(null);
          }}
          orderId={selectedOrderId}
        />
      )}
    </div>
  );
}
